/**
 * Ownership for client-created dorm rooms.
 *
 * The security rules resolve a room's permissions through
 * `room.structureId -> structure.projectId -> project.ownerId`. On staging and
 * prod the room CREATE resolves that chain too (`isIncomingRoomProjectOwner`),
 * so three things are mandatory, not advisory:
 *
 *   - the structure must exist BEFORE the room that points at it, or the create
 *     dereferences a missing document and is denied;
 *   - it must carry a `projectId` the caller owns, or the room can never be
 *     created (staging/prod) nor updated (everywhere) — which silently breaks
 *     layout apply, Style It and every later edit;
 *   - `createdBy` must be the caller's uid.
 *
 * The policy lives here, not just the utilities: a caller that merely *warns*
 * and writes anyway produces exactly the broken state this module exists to
 * prevent.
 */

import { auth } from '../../firebase';
import { projectService } from '../projectService';

/** The signed-in uid, or null. */
export function currentUid() {
  return auth.currentUser?.uid || null;
}

/** Name given to the project minted for an account that owns none yet. */
export const DEFAULT_DORM_PROJECT_NAME = 'My Dorm';

/**
 * The project a new dorm room must belong to: the caller's most recent OWNED
 * project (`projectService.getProjects()` filters on `ownerId`, so ownership is
 * guaranteed rather than assumed).
 *
 * An account that owns NO project gets one created here rather than an error.
 * Every admin has projects, so the old throw never fired for them — but a
 * student arriving at the dorm wizard on a fresh account has no way to make one
 * from the web app at all (its "Add New Project" card only tells you to use
 * iOS), so refusing would have been a dead end rather than a diagnosis.
 *
 * Still throws when there is no signed-in user: a dorm room without a project
 * is not a degraded room, it is an unusable one, and failing here names the
 * cause instead of surfacing "Missing or insufficient permissions" later.
 */
export async function requireOwnerProjectId() {
  if (!currentUid()) {
    throw new Error('Must be signed in to create a dorm room.');
  }
  const projects = await projectService.getProjects();
  const existing = projects?.[0]?.id;
  if (existing) return existing;

  const created = await projectService.createProject({
    name: DEFAULT_DORM_PROJECT_NAME,
  });
  return created.id;
}

/**
 * Stamp `createdBy` onto a doc about to be created. Throws rather than writing
 * an unowned doc, which the rules would reject anyway.
 */
export function withCreatedBy(data) {
  const uid = currentUid();
  if (!uid) {
    throw new Error('Must be signed in to create a dorm room.');
  }
  return { ...data, createdBy: uid };
}
